/**
 * The building blocks every synthesised sound is made of: a noise source and
 * two filters. Plain arithmetic on numbers, so they run in the audio thread,
 * in a worker and in tests alike.
 */

/** A small, fast generator: the sound is noise all the way down. */
export class Rng {
  private s: number;
  constructor(seed: number) {
    this.s = (seed >>> 0) || 0x9e3779b9;
  }
  /** Uniform in [0, 1). */
  next(): number {
    let x = this.s;
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    this.s = x >>> 0;
    return this.s / 4294967296;
  }
  /** Uniform in [-1, 1). */
  white(): number {
    return this.next() * 2 - 1;
  }
}

/** A state-variable filter (Zavalishin's TPT form): low, band and high at once, stable as the cut-off moves. */
export class Svf {
  private ic1 = 0;
  private ic2 = 0;
  private a1 = 0;
  private a2 = 0;
  private a3 = 0;
  private k = 1;
  lp = 0;
  bp = 0;
  hp = 0;

  set(cutoff: number, q: number, sampleRate: number): void {
    const fc = Math.min(cutoff, sampleRate * 0.45);
    const g = Math.tan((Math.PI * fc) / sampleRate);
    this.k = 1 / q;
    this.a1 = 1 / (1 + g * (g + this.k));
    this.a2 = g * this.a1;
    this.a3 = g * this.a2;
  }

  process(v0: number): void {
    const v3 = v0 - this.ic2;
    const v1 = this.a1 * this.ic1 + this.a2 * v3;
    const v2 = this.ic2 + this.a2 * this.ic1 + this.a3 * v3;
    this.ic1 = 2 * v1 - this.ic1;
    this.ic2 = 2 * v2 - this.ic2;
    this.lp = v2;
    this.bp = v1;
    this.hp = v0 - this.k * v1 - v2;
  }
}

/** A one-pole low-pass. */
export class OnePole {
  private y = 0;
  private a = 0;
  set(cutoff: number, sampleRate: number): void {
    this.a = 1 - Math.exp((-2 * Math.PI * Math.min(cutoff, sampleRate * 0.45)) / sampleRate);
  }
  process(x: number): number {
    this.y += this.a * (x - this.y);
    return this.y;
  }
}
